import { ReactNode } from "react";
import Icon, { iconExists } from "../icons/Icon";
import { IconName } from "../icons/iconNames";
import { emptyStateStyles } from "./EmptyStateStyles";

interface EmptyStateProps {
  // A design-system icon name (e.g. "chartBar", "bookOpen") shown in a brand
  // medallion. Convention: pick the glyph the screen's own nav/hero stands for,
  // so the empty state speaks the same icon language as the rest of the app.
  //
  // A raw emoji ("📊") is NOT acceptable here — multi-colour emoji ignore the
  // medallion's brand tint and clash with the app's Phosphor line glyphs. The
  // type can't express that (a `string` structurally satisfies ReactNode's
  // `Iterable<ReactNode>` member, so no Exclude<> trick rejects it), so it is
  // enforced by `npm run lint:no-emoji-icon` instead. Need a glyph we don't
  // have? Add the canonical name to iconNames.ts + every pack.
  //
  // Any non-string node is rendered as-is — the escape hatch for a bespoke
  // illustration.
  icon?: IconName | ReactNode;
  title: string;
  description?: string;
  // Optional CTA node (e.g. a <PrimaryButton/> or the screen's own selector).
  action?: ReactNode;
  // "compact" tightens padding for a per-tab empty inside an otherwise populated
  // screen (vs. the full-page empty). Purely spacing — same visual language.
  compact?: boolean;
}

// The single shared empty / "nothing here yet" / "make a selection" state for every
// learner screen. Replaces the ~14 hand-rolled per-screen variants (plain text, dashed
// card, inline ⓘ, missing) with one calm centered treatment on the frosted page.
const EmptyState = ({
  icon,
  title,
  description,
  action,
  compact = false,
}: EmptyStateProps) => {
  // A string that names a real glyph renders through the icon system; anything
  // else (a node, or a leftover emoji) is passed straight through — the medallion
  // sizes both off its own font-size. Mirrors the `iconExists` fallback pattern
  // used elsewhere for names that may not have a glyph.
  const medallion =
    typeof icon === "string" && iconExists(icon) ? (
      <Icon name={icon} size={34} />
    ) : (
      icon
    );
  return (
    <div className={`es-root${compact ? " es-root--compact" : ""}`}>
      <style>{emptyStateStyles}</style>
      {icon != null ? <div className="es-medallion">{medallion}</div> : null}
      <div className="es-title">{title}</div>
      {description ? <p className="es-desc">{description}</p> : null}
      {action ? <div className="es-action">{action}</div> : null}
    </div>
  );
};

export default EmptyState;
